/**
 * 検索ドロワーの候補。ブランドとキーワードを 1 つの listbox にまとめて出す。
 *
 * キーワードは c_search-keywords.liquid が出力した辞書、ブランドは c_search-brand-suggestion
 * セクションが返すファセット集計。同期で揃うキーワードを先に置き、非同期のブランドを後に
 * 足すことで、表示済みの候補が押し下げられないようにしている。
 * どちらかが更新されるたびに option の id を振り直して aria-activedescendant を保つ。
 *
 * fold はカタカナとひらがな、大文字と小文字を 1 文字ずつ畳む。文字数が変わらないため、
 * 一致位置をそのまま元の文字列に使える。半角カナと全角英数字は 1:1 に対応しないため扱わない。
 *
 * Esc はベーステーマの focus-trap が document の capture で握っており、候補だけを閉じる
 * 割り込みは初期化順に依存する。ドロワーごと閉じる挙動をそのまま使う。
 *
 * init は shopify:section:load でも呼ぶ。ベーステーマが append-body でドロワーのノードごと
 * 差し替えるため、購読を貼り直す必要がある。
 */
(() => {
  const SECTION_ID = 'c_search-brand-suggestion';
  const RESULTS_SECTION_ID = 'c_search-results';
  const DRAWER_SELECTOR = '#search-drawer';
  const INPUT_SELECTOR = '[name="q"]';
  const LISTBOX_SELECTOR = '.c_c-search-suggestions';
  const STATUS_SELECTOR = '[data-suggestion-status]';
  const KEYWORD_DATA_SELECTOR = 'script[data-search-keywords]';
  const DRAWER_FOOTER_SELECTOR = '.drawer__footer';
  const LOADING_SELECTOR = '.predictive-search__loading-state';
  const RESULTS_SELECTOR = '.predictive-search__results';
  const SEARCH_WORDS_SELECTOR = '.c_c-search-words';
  const RESULT_TYPES = ['product', 'article'];
  const TIMEOUT_REASON = 'timeout';
  const OPTION_ID_PREFIX = 'c_search-suggestion-';
  const INITIALIZED_FLAG = 'searchSuggestionsReady';

  const KEYWORD_LIMIT = 6;
  const BRAND_LIMIT = 6;
  // 検索候補と並ぶときは、アイテムタブがスクロールせず見える高さに収める
  const BRAND_LIMIT_WITH_KEYWORDS = 3;
  // 欧文の 1〜2 文字は打鍵の通過点でクリックに至らないが、漢字・カタカナの 2 文字は
  // 語として成立する（財布・時計）。ひらがなと長音符は「にー」など変換途中のため含めない
  const MIN_TERMS_LENGTH = 3;
  const MIN_CJK_TERMS_LENGTH = 2;
  const CJK_PATTERN = /[ァ-ヶ一-龠ｦ-ﾟ]/;
  const DEBOUNCE_MS = 200;
  // ブランド候補は designer_list_all の走査を含み 2 秒台まで伸びることがある
  const TIMEOUT_MS = 4000;
  // 結果は商品カード 10 枚を含み応答が大きいため、候補より長く待つ
  const RESULTS_TIMEOUT_MS = 6000;
  const ANNOUNCE_DELAY_MS = 500;

  const fold = (value) =>
    value.toLowerCase().replace(/[ァ-ヶ]/g, (character) =>
      String.fromCharCode(character.charCodeAt(0) - 96)
    );

  const escapeHtml = (value) =>
    String(value).replace(
      /[&<>"']/g,
      (character) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
    );

  const highlight = (label, terms) => {
    const index = fold(label).indexOf(fold(terms));

    if (index === -1) {
      return escapeHtml(label);
    }

    return (
      escapeHtml(label.slice(0, index)) +
      '<mark>' +
      escapeHtml(label.slice(index, index + terms.length)) +
      '</mark>' +
      escapeHtml(label.slice(index + terms.length))
    );
  };

  class SearchSuggestions {
    constructor(drawerElement) {
      this.drawerElement = drawerElement;
      this.inputElement = drawerElement.querySelector(INPUT_SELECTOR);
      this.listboxElement = drawerElement.querySelector(LISTBOX_SELECTOR);
      this.statusElement = drawerElement.querySelector(STATUS_SELECTOR);

      if (!this.inputElement || !this.listboxElement || !this.statusElement) {
        return;
      }

      this.groupElements = {};
      this.listElements = {};

      for (const groupElement of this.listboxElement.querySelectorAll('[data-suggestion-group]')) {
        const name = groupElement.dataset.suggestionGroup;

        this.groupElements[name] = groupElement;
        this.listElements[name] = groupElement.querySelector('[data-suggestion-list]');
      }

      this.keywords = this._readKeywords();
      this.options = [];
      this.activeIndex = -1;
      this.abortController = null;
      this.resultsController = null;
      this.hasKeywords = false;
      this.debounceTimer = null;
      this.announceTimer = null;

      this.inputElement.addEventListener('input', this._blockDrawerSearch.bind(this));
      this.inputElement.addEventListener('input', this._onInput.bind(this));
      this.inputElement.addEventListener('compositionend', this._onCompositionEnd.bind(this));
      this.inputElement.addEventListener('keydown', this._onKeyDown.bind(this));
      this.listboxElement.addEventListener('click', this._onClick.bind(this));
    }

    _readKeywords() {
      const dataElement = document.querySelector(KEYWORD_DATA_SELECTOR);

      if (!dataElement) {
        return [];
      }

      try {
        return JSON.parse(dataElement.textContent);
      } catch {
        return [];
      }
    }

    /**
     * 検索結果はこのファイルが描くため、入力をベーステーマのドロワー検索へ伝えない。
     * ベーステーマはドロワー要素で委譲して受けるので、入力要素で止めれば届かない。
     */
    _blockDrawerSearch(event) {
      event.stopPropagation();
    }

    _meetsMinLength(terms) {
      if (CJK_PATTERN.test(terms)) {
        return terms.length >= MIN_CJK_TERMS_LENGTH;
      }

      return terms.length >= MIN_TERMS_LENGTH;
    }

    /**
     * 確定後に input を発火しないブラウザがあるため、自分で発火させる。
     * 発火するブラウザではデバウンスに吸収される。
     */
    _onCompositionEnd() {
      this.inputElement.dispatchEvent(new InputEvent('input', { bubbles: true }));
    }

    /**
     * 検索する長さを下回ったときに前の検索結果を残さないよう、初期表示に戻す。
     */
    _resetDrawerResults() {
      const resultsElement = this.drawerElement.querySelector(RESULTS_SELECTOR);

      if (this.resultsController) {
        this.resultsController.abort();
        this.resultsController = null;
      }

      if (resultsElement) {
        resultsElement.innerHTML = '';
      }

      this._setDrawerPartHidden(DRAWER_FOOTER_SELECTOR, true);
      this._setDrawerPartHidden(LOADING_SELECTOR, true);
      this._setDrawerPartHidden(RESULTS_SELECTOR, true);
    }

    _setDrawerPartHidden(selector, hidden) {
      const element = this.drawerElement.querySelector(selector);

      if (element) {
        element.hidden = hidden;
      }
    }

    _onInput(event) {
      clearTimeout(this.debounceTimer);

      if (event.isComposing) {
        // デバウンスを止めた分、確定まで回り続けるスピナーは消す
        this._setDrawerPartHidden(LOADING_SELECTOR, true);
        return;
      }

      const terms = this.inputElement.value.trim();
      const meetsMinLength = this._meetsMinLength(terms);

      // 候補も結果も出さない長さでは、ドロワーが空にならないよう検索ワードを残す
      this._setDrawerPartHidden(SEARCH_WORDS_SELECTOR, meetsMinLength);

      if (!meetsMinLength) {
        this._close();
        this._resetDrawerResults();
        return;
      }

      const keywords = this._matchKeywords(terms);

      this.hasKeywords = keywords.length > 0;

      // 前のクエリの結果を残すと、無関係な候補が選択可能なまま件数も誤って通知される
      this._renderGroup('brand', [], terms);
      this._renderGroup('keyword', keywords, terms);

      // 表示中の結果に重ねない。打ち足すたびにスピナーが点滅するため
      if (this.drawerElement.querySelector(RESULTS_SELECTOR)?.hidden !== false) {
        this._setDrawerPartHidden(LOADING_SELECTOR, false);
      }

      this.debounceTimer = setTimeout(() => {
        this._fetchBrands(terms);
        this._fetchResults(terms);
      }, DEBOUNCE_MS);
    }

    _buildResultsUrl(terms, type) {
      const parameters = new URLSearchParams({
        section_id: RESULTS_SECTION_ID,
        type,
        q: terms,
        'options[prefix]': 'last',
        'options[unavailable_products]': window.themeVariables.settings.searchUnavailableProducts
      });

      return `${window.themeVariables.routes.searchUrl}?${parameters}`;
    }

    /**
     * 種別ごとに 1 回ずつ取得する。検索結果は種別を跨いで順位付けされ、
     * 商品が上位を占めて記事が 1 ページ目に載らないため 1 回にまとめられない。
     */
    async _fetchResults(terms) {
      if (this.resultsController) {
        this.resultsController.abort();
      }

      this.resultsController = new AbortController();

      const controller = this.resultsController;
      const timeoutId = setTimeout(() => controller.abort(TIMEOUT_REASON), RESULTS_TIMEOUT_MS);

      // 片方が落ちてももう片方は出す。商品と記事は独立した検索のため
      const settled = await Promise.allSettled(
        RESULT_TYPES.map((type) =>
          fetch(this._buildResultsUrl(terms, type), { signal: controller.signal }).then((response) => {
            if (!response.ok) {
              throw new Error(`search results request failed: ${response.status}`);
            }

            return response.text();
          })
        )
      );

      clearTimeout(timeoutId);

      if (this.inputElement.value.trim() !== terms) {
        return;
      }

      if (settled.every((result) => result.status === 'rejected')) {
        // 後続の入力に追い越されただけなら、次の取得が続くのでスピナーは残す
        if (controller.signal.aborted && controller.signal.reason !== TIMEOUT_REASON) {
          return;
        }

        const resultsElement = this.drawerElement.querySelector(RESULTS_SELECTOR);

        if (resultsElement) {
          resultsElement.innerHTML = '';
        }

        // 古い結果を残すと、フッターの遷移先と表示が食い違う
        this._setDrawerPartHidden(RESULTS_SELECTOR, true);
        this._setDrawerPartHidden(LOADING_SELECTOR, true);
        this._setDrawerPartHidden(DRAWER_FOOTER_SELECTOR, false);
        return;
      }

      this._renderResults(settled.map((result) => (result.status === 'fulfilled' ? result.value : '')));
    }

    _renderResults(responses) {
      const panels = responses
        .map((text, index) => ({ type: RESULT_TYPES[index], section: this._extractSection(text) }))
        .filter((panel) => panel.section);

      const resultsElement = this.drawerElement.querySelector(RESULTS_SELECTOR);

      this._setDrawerPartHidden(LOADING_SELECTOR, true);

      if (!resultsElement) {
        return;
      }

      // tabs-nav は挿入時に下線の幅を測るため、先に hidden を外して幅を確定させる
      this._setDrawerPartHidden(RESULTS_SELECTOR, false);

      if (panels.length === 0) {
        // reset-search はベーステーマの委譲リスナーが click で拾う。止めているのは input だけ
        resultsElement.innerHTML =
          `<p class="text--large">${escapeHtml(window.themeVariables.strings.searchNoResults)}</p>` +
          `<div class="button-wrapper"><button type="button" data-action="reset-search" class="button button--primary">${escapeHtml(window.themeVariables.strings.searchNewSearch)}</button></div>`;
        this._setDrawerPartHidden(DRAWER_FOOTER_SELECTOR, true);
        return;
      }

      resultsElement.replaceChildren(this._buildTabs(panels), this._buildPanels(panels));
      this._setDrawerPartHidden(DRAWER_FOOTER_SELECTOR, false);
    }

    /**
     * Section Rendering API の応答は section の div で包まれる。文字列に戻すと
     * 商品カードの img を二度組み立てることになるため、要素のまま返す。
     */
    _extractSection(responseText) {
      const holderElement = document.createElement('div');

      holderElement.innerHTML = responseText;

      const sectionElement = holderElement.firstElementChild;

      // 0 件のセクションは空でも改行だけのテキストノードを持つため、要素の有無で見る
      return sectionElement?.firstElementChild ? sectionElement : null;
    }

    _buildTabs(panels) {
      // 切り替えはベーステーマの tabs-nav 要素が担うため、同じ構造で出す
      const items = panels
        .map((panel, index) => {
          const label = window.themeVariables.strings[panel.type === 'product' ? 'searchProducts' : 'searchArticles'];

          return `<button type="button" class="tabs-nav__item heading heading--small" aria-expanded="${index === 0}" aria-controls="${RESULTS_SECTION_ID}-${panel.type}">${escapeHtml(label)}</button>`;
        })
        .join('');

      const navElement = document.createElement('tabs-nav');

      navElement.className = 'tabs-nav tabs-nav--edge2edge tabs-nav--narrow tabs-nav--no-border';
      navElement.innerHTML = `<scrollable-content class="tabs-nav__scroller hide-scrollbar"><div class="tabs-nav__scroller-inner"><div class="tabs-nav__item-list">${items}</div></div></scrollable-content>`;

      return navElement;
    }

    _buildPanels(panels) {
      const containerElement = document.createElement('div');

      containerElement.className = 'predictive-search__results-categories';

      for (const [index, panel] of panels.entries()) {
        const itemElement = document.createElement('div');

        itemElement.className = 'predictive-search__results-categories-item';
        itemElement.id = `${RESULTS_SECTION_ID}-${panel.type}`;
        itemElement.hidden = index !== 0;
        itemElement.append(...panel.section.childNodes);
        containerElement.append(itemElement);
      }

      return containerElement;
    }

    _limitBrands(brands) {
      if (this.hasKeywords) {
        return brands.slice(0, BRAND_LIMIT_WITH_KEYWORDS);
      }

      return brands.slice(0, BRAND_LIMIT);
    }

    _matchKeywords(terms) {
      const folded = fold(terms);

      return this.keywords
        .filter((keyword) => fold(keyword.label).includes(folded))
        .slice(0, KEYWORD_LIMIT);
    }

    _close() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this._renderGroup('brand', [], '');
      this._renderGroup('keyword', [], '');
    }

    _buildRequestUrl(terms) {
      const parameters = new URLSearchParams({
        section_id: SECTION_ID,
        type: 'product',
        q: terms,
        'options[prefix]': 'last',
        'options[unavailable_products]': window.themeVariables.settings.searchUnavailableProducts
      });

      return `${window.themeVariables.routes.searchUrl}?${parameters}`;
    }

    /**
     * Section Rendering API の応答は section の div で包まれるため、
     * script タグを DOM から取り出して JSON を得る。
     */
    _parsePayload(responseText) {
      const holderElement = document.createElement('div');

      holderElement.innerHTML = responseText;

      const dataElement = holderElement.querySelector('script[data-brand-suggestion]');

      if (!dataElement) {
        throw new Error('brand suggestion data not found');
      }

      return JSON.parse(dataElement.textContent);
    }

    async _fetchBrands(terms) {
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      const controller = this.abortController;
      const timeoutId = setTimeout(() => controller.abort(TIMEOUT_REASON), TIMEOUT_MS);

      try {
        const response = await fetch(this._buildRequestUrl(terms), {
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`brand suggestion request failed: ${response.status}`);
        }

        const payload = this._parsePayload(await response.text());

        if (this.inputElement.value.trim() !== terms) {
          return;
        }

        this._renderGroup('brand', this._limitBrands(payload.brands || []), terms);
      } catch (error) {
        // 後続の入力に追い越されただけなら、次の取得が描き直すので触らない
        if (error.name === 'AbortError' && controller.signal.reason !== TIMEOUT_REASON) {
          return;
        }

        this._renderGroup('brand', [], terms);
      } finally {
        clearTimeout(timeoutId);
      }
    }

    _renderGroup(name, entries, terms) {
      const groupElement = this.groupElements[name];
      const listElement = this.listElements[name];

      if (!groupElement || !listElement) {
        return;
      }

      if (!Array.isArray(entries) || entries.length === 0) {
        listElement.innerHTML = '';
        groupElement.hidden = true;
        this._syncOptions();
        return;
      }

      listElement.innerHTML = entries
        .map(
          (entry) =>
            `<div class="c_c-search-suggestions__item" role="option" aria-selected="false">` +
            `<a class="c_c-search-suggestions__link" href="${escapeHtml(entry.url)}" tabindex="-1">${highlight(entry.label, terms)}</a>` +
            `</div>`
        )
        .join('');
      groupElement.hidden = false;
      this._syncOptions();
    }

    /**
     * 2 つのグループは別々のタイミングで更新されるため、描画のたびに
     * option の id と選択状態を DOM 順で振り直す。
     */
    _syncOptions() {
      const activeElement = this.activeIndex >= 0 ? this.options[this.activeIndex] : null;

      this.options = Array.from(this.listboxElement.querySelectorAll('[role="option"]'));
      this.activeIndex = -1;
      this.inputElement.removeAttribute('aria-activedescendant');

      this.options.forEach((optionElement, index) => {
        optionElement.id = `${OPTION_ID_PREFIX}${index}`;
        optionElement.setAttribute('aria-selected', 'false');
      });

      const hasOptions = this.options.length > 0;

      this.listboxElement.hidden = !hasOptions;
      this.inputElement.setAttribute('aria-expanded', String(hasOptions));

      // 片方のグループだけが更新されたときは、選択したままの候補に選択を戻す
      const restoredIndex = activeElement ? this.options.indexOf(activeElement) : -1;

      if (restoredIndex >= 0) {
        this._setActive(restoredIndex);
      }

      this._announce(this.options.length);
    }

    _announce(count) {
      // 打鍵ごとに読み上げると入力のエコーに割り込むため、落ち着いてから通知する
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        const template = this.statusElement.dataset.statusTemplate || '';

        this.statusElement.textContent = count === 0 ? '' : template.replace('__COUNT__', count);
      }, ANNOUNCE_DELAY_MS);
    }

    _setActive(index) {
      if (this.activeIndex >= 0) {
        this.options[this.activeIndex].setAttribute('aria-selected', 'false');
      }

      this.activeIndex = index;

      const optionElement = this.options[index];

      optionElement.setAttribute('aria-selected', 'true');
      this.inputElement.setAttribute('aria-activedescendant', optionElement.id);
      optionElement.scrollIntoView({ block: 'nearest' });
    }

    _onKeyDown(event) {
      // 変換中の Enter や矢印キーは IME のものなので触らない
      if (event.isComposing || event.keyCode === 229 || this.options.length === 0) {
        return;
      }

      const lastIndex = this.options.length - 1;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this._setActive(this.activeIndex >= lastIndex ? 0 : this.activeIndex + 1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this._setActive(this.activeIndex <= 0 ? lastIndex : this.activeIndex - 1);
        return;
      }

      // 選択が無いときはフォームの送信に任せ、通常の検索を実行させる
      if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.options[this.activeIndex].querySelector('a').click();
      }
    }

    _onClick(event) {
      const optionElement = event.target.closest('[role="option"]');

      // リンク上のクリックは既定の遷移に任せる。mark など内側の要素も含めて判定する
      if (optionElement && !event.target.closest('a')) {
        optionElement.querySelector('a').click();
      }
    }
  }

  const init = () => {
    const drawerElement = document.querySelector(DRAWER_SELECTOR);

    if (drawerElement && !drawerElement.dataset[INITIALIZED_FLAG]) {
      drawerElement.dataset[INITIALIZED_FLAG] = 'true';
      new SearchSuggestions(drawerElement);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
